/**
 * RoadmapAgentPanel.tsx — floating chat panel for the SSI roadmap agent, plus
 * the header launcher and the corner button that replaces Zendesk chat on
 * Tickets, Roadmap, and Sprints. Conversation state lives in the shared
 * roadmap-chat store so the thread survives navigating between roadmap pages.
 * Assistant replies render a deliberately small markdown subset (bold, inline
 * code, bullets, links) — internal links become client-side router links so
 * "[FR-012](/admin/roadmap/requests/12)" navigates without a reload.
 *
 * The composer auto-grows for long messages and accepts screenshot attachments
 * that the model can see and that land on any FR created in that turn.
 */

import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Plus, Send, Sparkles, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useIsSSI } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { isRoadmapWorkspacePath } from '../lib/roadmap-workspace';
import { AgentMarkdown } from './RoadmapAgentMarkdown';
import { useRoadmapChatStore, type RoadmapChatMessage } from '../stores/roadmap-chat.store';

const SUGGESTIONS = [
  'What shipped recently?',
  'What is blocked right now?',
  'Top 5 ranked feature requests',
];

const ACCEPT_IMAGES = 'image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif';
const TEXTAREA_MIN_PX = 40;
const TEXTAREA_MAX_PX = 160;

function MessageBubble({ message }: { message: RoadmapChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[88%] space-y-1.5 rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-[13px] leading-6 text-primary-foreground">
          {message.imagePreviewUrls?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {message.imagePreviewUrls.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt=""
                  className="h-16 w-16 rounded-md object-cover ring-1 ring-primary-foreground/30"
                />
              ))}
            </div>
          ) : null}
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[94%] space-y-1.5">
      {message.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {message.activity ?? 'Thinking…'}
        </div>
      ) : (
        <AgentMarkdown content={message.content} />
      )}
      {message.error ? (
        <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive-text">
          {message.error}
        </div>
      ) : null}
    </div>
  );
}

function RoadmapAgentPanel({ onClose }: { onClose: () => void }) {
  const messages = useRoadmapChatStore((s) => s.messages);
  const isLoading = useRoadmapChatStore((s) => s.isLoading);
  const submitIntent = useRoadmapChatStore((s) => s.submitIntent);
  const pendingImages = useRoadmapChatStore((s) => s.pendingImages);
  const sendMessage = useRoadmapChatStore((s) => s.sendMessage);
  const addPendingImages = useRoadmapChatStore((s) => s.addPendingImages);
  const removePendingImage = useRoadmapChatStore((s) => s.removePendingImage);
  const clear = useRoadmapChatStore((s) => s.clear);
  const [input, setInput] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
    el.style.height = `${next}px`;
  }, [input]);

  const canSend = (input.trim().length > 0 || pendingImages.length > 0) && !isLoading;

  const submit = (text: string) => {
    if (!canSend && !text.trim()) return;
    setInput('');
    void sendMessage(text);
  };

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    const err = addPendingImages(Array.from(fileList));
    if (err) toast.error(err);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      style={{
        bottom: 'max(1rem, env(safe-area-inset-bottom))',
        right: 'max(1rem, env(safe-area-inset-right))',
      }}
      className="fixed z-50 flex h-[min(76vh,680px)] w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
    >
      <div className="flex items-center justify-between border-b border-border bg-card px-3 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Sparkles className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">
              {submitIntent ? 'Submit with agent' : 'Roadmap agent'}
            </h3>
            <p className="truncate text-[11px] text-muted-foreground">
              {submitIntent ? 'Search first, then a short draft' : 'Roadmap and backlog'}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={clear}
            title="New conversation"
            aria-label="New conversation"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={messagesRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 text-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <p className="text-sm font-medium text-foreground">
              {submitIntent ? 'Describe the issue' : 'Ask about the backlog'}
            </p>
            <p className="mt-1 max-w-[16rem] text-[12px] leading-5 text-muted-foreground">
              {submitIntent
                ? 'Paste a screenshot or type it. Matching tickets show up before anything new is filed.'
                : 'Shipped work, blockers, or a new ticket. Existing matches come first.'}
            </p>
          </div>
        ) : null}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>

      {messages.length === 0 && !submitIntent ? (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {SUGGESTIONS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => submit(q)}
              disabled={isLoading}
              className="rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
            >
              {q}
            </button>
          ))}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="border-t border-border bg-card px-3 py-3 space-y-2"
      >
        {pendingImages.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {pendingImages.map((img) => (
              <li key={img.id} className="relative">
                <img
                  src={img.previewUrl}
                  alt={img.file.name}
                  className="h-14 w-14 rounded-md object-cover ring-1 ring-border"
                />
                <button
                  type="button"
                  aria-label={`Remove ${img.file.name}`}
                  className="absolute -right-1 -top-1 rounded-full bg-background p-0.5 text-muted-foreground shadow ring-1 ring-border hover:text-destructive-text"
                  onClick={() => removePendingImage(img.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="rounded-xl border border-border bg-background p-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
            placeholder={
              submitIntent ? 'Describe it or paste a screenshot…' : 'Ask about the roadmap…'
            }
            disabled={isLoading}
            rows={1}
            className="max-h-40 min-h-[40px] w-full resize-none overflow-y-auto bg-transparent px-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              title="Attach images"
              disabled={isLoading || pendingImages.length >= 3}
              onClick={() => fileInputRef.current?.click()}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <ImagePlus className="h-4 w-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_IMAGES}
              multiple
              className="hidden"
              aria-label="Attach images to the agent message"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send"
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                canSend
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/**
 * Header button for the roadmap agent. The panel itself is mounted once by
 * {@link RoadmapAgentHost}, so this only toggles the shared open state.
 */
export function RoadmapAgentLauncher() {
  const isOpen = useRoadmapChatStore((s) => s.isOpen);
  const setOpen = useRoadmapChatStore((s) => s.setOpen);
  function handleToggle() {
    setOpen(!isOpen);
  }
  return (
    <Button variant="outline" size="sm" onClick={handleToggle}>
      <Sparkles className="h-4 w-4" aria-hidden />
      Roadmap agent
    </Button>
  );
}

/**
 * Corner control on Tickets, Roadmap, and Sprints. Takes the spot Zendesk
 * chat uses everywhere else, so the chat bubble is not sitting on the board.
 */
export function RoadmapAgentHost() {
  const { pathname } = useLocation();
  const isSSI = useIsSSI();
  const isOpen = useRoadmapChatStore((s) => s.isOpen);
  const setOpen = useRoadmapChatStore((s) => s.setOpen);
  const onWorkspace = isSSI && isRoadmapWorkspacePath(pathname);

  useEffect(() => {
    if (onWorkspace || !useRoadmapChatStore.getState().isOpen) return;
    setOpen(false);
  }, [onWorkspace, setOpen]);

  function handleOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  if (!onWorkspace) return null;

  if (isOpen) return <RoadmapAgentPanel onClose={handleClose} />;

  return (
    <button
      type="button"
      onClick={handleOpen}
      style={{
        bottom: 'max(1rem, env(safe-area-inset-bottom))',
        right: 'max(1rem, env(safe-area-inset-right))',
      }}
      className="fixed z-sidebar flex h-13 w-13 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-shadow hover:bg-primary/90 hover:shadow-xl active:scale-95"
      aria-label="Open roadmap agent"
      title="Roadmap agent"
    >
      <Sparkles className="h-6 w-6" aria-hidden />
    </button>
  );
}
